import React, { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDidUpdateEffect } from '@odin-labs/components';
import {
  useGetAnonymousJobsiteInvitationQuery,
  useGetJobsiteInvitationQuery,
  useGetSelfOnboardingContainerSessionQuery,
} from 'apollo/generated/client-operations';
import { isEmpty } from 'utils';
import { PolicyDocumentFragment } from 'containers/policyConsent';
import { localizeFactory } from 'utils/localization';
import { useLegacyState } from 'utils/useLegacyState';
import { useQueryParams } from 'utils/useQueryParams';
import { LoadingError } from 'components/loadingError';
import { JobsiteFeatureFlagsProvider, useFlags } from 'featureFlags';
import { AnonymousJobsiteInvitation } from './helpers/anonymousJobsiteInvitation';
import { defaultLanguage } from './utils';
import {
  SelfOnboardingState,
  SelfOnboardingStepComponent,
  SelfOnboardingStepKey,
} from './types';
import {
  LanguageSelectionStep,
  ContractorSelectionStep,
  LoginStartStep,
  LoginCompleteStep,
  WorkerNameStep,
  BasicInfoStep,
  ProfileStep,
  EmergencyContactStep,
  DocumentStep,
  MatchedWorkerStep,
  ErrorStep,
  ProfilePhotoStep,
  ReviewCompletedProfileStep,
  VerifyMobileDeviceStartStep,
  VerifyMobileDeviceCompleteStep,
  VerifyMobileDeviceToSignInStartStep,
  VerifyMobileDeviceToSignInCompleteStep,
  UnableToVerifyAccountStep,
  JobsiteWorkerWithDifferentContractorStep,
} from './steps';
import { getStepConfig } from './helpers/utils';
import { classes } from './SelfOnboardingContainer.style';
import { SelfOnboardingHeader } from './SelfOnboardingHeader';
import {
  getSelfOnboardingLanguageDependentState,
  getSelfOnboardingState,
} from './SelfOnboardingContainer.forms';
import { useSelfOnboardingNavigation } from './helpers/useSelfOnboardingNavigation';
import { AuthenticatedSelfOnboardingFlow } from './AuthenticatedSelfOnboardingFlow';

const componentsByStep: Record<
  SelfOnboardingStepKey,
  SelfOnboardingStepComponent
> = {
  [SelfOnboardingStepKey.LanguageSelection]: LanguageSelectionStep,
  [SelfOnboardingStepKey.ContractorSelection]: ContractorSelectionStep,
  [SelfOnboardingStepKey.Login]: LoginStartStep,
  [SelfOnboardingStepKey.ConfirmLoginCode]: LoginCompleteStep,
  [SelfOnboardingStepKey.WorkerName]: WorkerNameStep,
  [SelfOnboardingStepKey.BasicInfo]: BasicInfoStep,
  [SelfOnboardingStepKey.WorkerConsentDocument]: DocumentStep,
  [SelfOnboardingStepKey.Profile]: ProfileStep,
  [SelfOnboardingStepKey.EmergencyContactInfo]: EmergencyContactStep,
  [SelfOnboardingStepKey.GovernmentIssuedId]: DocumentStep,
  [SelfOnboardingStepKey.NycSiteSafetyTrainingCard]: DocumentStep,
  [SelfOnboardingStepKey.OshaCard]: DocumentStep,
  [SelfOnboardingStepKey.TwicCard]: DocumentStep,
  [SelfOnboardingStepKey.JobsiteSafetyVideo]: DocumentStep,
  [SelfOnboardingStepKey.JobsiteSafetyDocument]: DocumentStep,
  [SelfOnboardingStepKey.LIRRBlueTraining]: DocumentStep,
  [SelfOnboardingStepKey.MustSafetyReport]: DocumentStep,
  [SelfOnboardingStepKey.ConfinedSpacesTraining]: DocumentStep,
  [SelfOnboardingStepKey.ProfilePhoto]: ProfilePhotoStep,
  [SelfOnboardingStepKey.VerifyMobileDevice]: VerifyMobileDeviceStartStep,
  [SelfOnboardingStepKey.ConfirmMobileDevice]: VerifyMobileDeviceCompleteStep,
  [SelfOnboardingStepKey.VerifyMobileDeviceToSignIn]:
    VerifyMobileDeviceToSignInStartStep,
  [SelfOnboardingStepKey.ConfirmMobileDeviceToSignIn]:
    VerifyMobileDeviceToSignInCompleteStep,
  [SelfOnboardingStepKey.ReviewCompletedProfile]: ReviewCompletedProfileStep,
  [SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor]:
    JobsiteWorkerWithDifferentContractorStep,
  [SelfOnboardingStepKey.AlreadyCompleted]: MatchedWorkerStep,
  [SelfOnboardingStepKey.Closed]: MatchedWorkerStep,
  [SelfOnboardingStepKey.UnableToVerifyAccount]: UnableToVerifyAccountStep,
};

const noHeaderSteps: SelfOnboardingStepKey[] = [
  SelfOnboardingStepKey.LanguageSelection,
];

const initialState: SelfOnboardingState = {} as SelfOnboardingState;

type SelfOnboardingFlowProps = {
  anonymousJobsiteInvitation?: AnonymousJobsiteInvitation;
  currentPolicyDocuments?: PolicyDocumentFragment[];
};

function SelfOnboardingFlow({
  anonymousJobsiteInvitation,
}: SelfOnboardingFlowProps): ReactElement {
  const { jobsiteInvitationId } = useParams<{
    jobsiteInvitationId: string;
    step: string;
  }>();
  const urlSearchParams = useQueryParams();
  const contractorId = urlSearchParams.get('contractorId');

  const [state, updateState] =
    useLegacyState<SelfOnboardingState>(initialState);
  const [language, setLanguage] = useState(defaultLanguage);

  // Session jobsite/contractor come from the always-available anonymous
  // invitation, so the session query doesn't depend on the full one.
  const sessionJobsiteId = anonymousJobsiteInvitation?.jobsiteId;
  const sessionContractorId =
    contractorId ?? anonymousJobsiteInvitation?.contractor?.contractorId;

  const {
    data: selfOnboardingData,
    loading: selfOnboardingLoading,
    error: selfOnboardingError,
    refetch: refetchSelfOnboardingData,
  } = useGetSelfOnboardingContainerSessionQuery({
    fetchPolicy: 'no-cache',
    skip: !sessionJobsiteId,
    variables: {
      jobsiteWorkersInput: {
        jobsiteIds: [sessionJobsiteId],
        contractorIds: [sessionContractorId],
      },
      workerDocumentsInput: { jobsiteIds: [sessionJobsiteId] },
    },
  });
  const selfOnboardingSession = selfOnboardingData?.getCurrentSession;
  const { user: selfOnboardingUser } = selfOnboardingSession ?? {};

  const {
    data: jobsiteInvitationData,
    loading: jobsiteInvitationLoading,
    error: jobsiteInvitationError,
  } = useGetJobsiteInvitationQuery({
    fetchPolicy: 'no-cache',
    variables: { jobsiteInvitationId },
  });
  const jobsiteInvitation = jobsiteInvitationData?.getJobsiteInvitation;
  const { jobsite } = jobsiteInvitation?.jobsiteContractor ?? {};

  const navigation = useSelfOnboardingNavigation({
    jobsite,
    selfOnboardingState: state,
  });

  const { currentStep, currentConfigStep, state: navigationState } = navigation;
  const { isReview } = navigationState;

  React.useEffect(() => {
    if (selfOnboardingData && jobsiteInvitation) {
      const selfOnboardingState = getSelfOnboardingState({
        user: selfOnboardingUser,
        jobsiteInvitation,
        contractorId,
        language,
      });
      updateState(selfOnboardingState);
    }
  }, [
    selfOnboardingData,
    jobsiteInvitation,
    contractorId,
    // language // it will be handled within a separate useEffect to update language dependent state only
  ]);

  // update language dependent state
  useDidUpdateEffect(() => {
    if (selfOnboardingData && jobsiteInvitation) {
      const languageDependentState = getSelfOnboardingLanguageDependentState({
        user: selfOnboardingUser,
        jobsiteInvitation,
        language,
      });
      updateState(languageDependentState);
    }
  }, [selfOnboardingData, jobsiteInvitation, language]);

  const stepConfig = React.useMemo(
    () => getStepConfig(jobsite?.modules, currentStep),
    [jobsite?.modules, currentStep],
  );
  const localize = localizeFactory(language.value);

  const loading =
    jobsiteInvitationLoading ||
    selfOnboardingLoading ||
    navigation.loading ||
    isEmpty(state);
  const error = jobsiteInvitationError || selfOnboardingError;

  if (!error && loading) {
    return <LoadingError loading={loading} />;
  }

  const getCurrentStepContent = (): ReactElement => {
    const StepComponent: SelfOnboardingStepComponent = error
      ? ErrorStep
      : componentsByStep[currentConfigStep];
    return (
      <StepComponent
        key={currentStep}
        loading={loading}
        refetchData={refetchSelfOnboardingData}
        user={selfOnboardingUser}
        state={state}
        updateState={updateState}
        navigation={navigation}
        jobsiteInvitation={jobsiteInvitation}
        stepConfig={stepConfig}
        language={language.value}
        onLanguageChange={setLanguage}
        localize={localize}
      />
    );
  };

  const withBackground =
    !error && currentStep === SelfOnboardingStepKey.LanguageSelection;
  const isHeaderVisible = !!error || !noHeaderSteps.includes(currentConfigStep);
  const canGoToPreviousStep =
    !error && !isReview && navigation.canGoToPreviousStep(currentStep);

  return (
    <div className={classes.fullContainer(withBackground)}>
      {isHeaderVisible && (
        <SelfOnboardingHeader
          language={language.value}
          onLanguageChange={setLanguage}
          localize={localize}
          navigation={navigation}
          canGoToPreviousStep={canGoToPreviousStep}
          state={state}
        />
      )}
      <div className={classes.container}>
        <div className={classes.stepContainer}>{getCurrentStepContent()}</div>
      </div>
    </div>
  );
}

/**
 * Selects the self-onboarding flow **once**, from inside the jobsite-scoped flag
 * provider: the isolated authenticated flow when the jobsite has it enabled,
 * otherwise the legacy flow. This is the single point the flag is read — neither
 * flow threads it internally.
 */
function SelfOnboardingFlowSelector({
  anonymousJobsiteInvitation,
  currentPolicyDocuments,
}: SelfOnboardingFlowProps): ReactElement {
  const { authenticatedSelfOnboardingEnabled } = useFlags();

  if (authenticatedSelfOnboardingEnabled) {
    return (
      <AuthenticatedSelfOnboardingFlow
        jobsiteId={anonymousJobsiteInvitation?.jobsiteId}
        contractorId={anonymousJobsiteInvitation?.contractor?.contractorId}
        currentPolicyDocuments={currentPolicyDocuments}
      />
    );
  }

  return (
    <SelfOnboardingFlow
      anonymousJobsiteInvitation={anonymousJobsiteInvitation}
    />
  );
}

/**
 * Bootstraps the jobsite-scoped feature-flag provider around the self-onboarding
 * flow. Resolves the jobsite id from the (always pre-auth-safe, minimal)
 * anonymous invitation query so jobsite-targeted flags — notably
 * `authenticatedSelfOnboardingEnabled` — are available for the flow selection
 * even before the worker authenticates. When the invitation can't be resolved,
 * the legacy flow is rendered so it surfaces its own error UI.
 */
export function SelfOnboardingContainer(): ReactElement {
  const { jobsiteInvitationId } = useParams<{
    jobsiteInvitationId: string;
    step: string;
  }>();
  const { data, loading } = useGetAnonymousJobsiteInvitationQuery({
    fetchPolicy: 'no-cache',
    variables: { jobsiteInvitationId },
  });
  const anonymousJobsiteInvitation = data?.getAnonymousJobsiteInvitation;
  const currentPolicyDocuments = data?.getCurrentPolicyDocuments;
  const jobsiteId = anonymousJobsiteInvitation?.jobsiteId;

  if (loading) {
    return <LoadingError loading />;
  }

  if (!jobsiteId) {
    return (
      <SelfOnboardingFlow
        anonymousJobsiteInvitation={anonymousJobsiteInvitation}
      />
    );
  }

  return (
    <JobsiteFeatureFlagsProvider jobsiteId={jobsiteId}>
      <SelfOnboardingFlowSelector
        anonymousJobsiteInvitation={anonymousJobsiteInvitation}
        currentPolicyDocuments={currentPolicyDocuments}
      />
    </JobsiteFeatureFlagsProvider>
  );
}
